import {
  Banknote,
  Building2,
  CircleUserRound,
  Clock,
  HandCoins,
  LucidePiggyBank,
  Store,
} from 'lucide-vue-next'
import RerunWebhookButton from 'pejer/src/components/account/RerunWebhookButton.vue'
import MoneyFormatted from 'pejer/src/components/MoneyFormatted.vue'
import ColumnLink from 'pejer/src/components/Table/core/ColumnLink.vue'
import DataTableGoto from 'pejer/src/components/Table/core/DataTableGoto.vue'
import { h } from 'vue'
import { Groups, JSSchema } from '.'
import { ConnectedAccountTypeList, CurrencyList, MoneyFormatter } from './Global'
import { ColorPalette } from './helpers'

export type ConnectedAccountType = 'company' | 'individual' | 'unknown'

export namespace ConnectedAccounts {
  export interface Settings {
    name: string | null
    email: string | null
  }

  export interface Full extends ListItem {}
  export interface ListItem extends Settings {
    // In norwegian Øre, 100 Øre = 1kr
    paymentCount: number
    revenueSum: number
    paidOutSum: number
    balance: number
    availableForPayouts: number

    connectedAccountID: string
    platformID: string
    externalReferenceID: string
    name: string | null
    payoutsEnabled: boolean
    salesEnabled: boolean
    businessName: string
    businessType: ConnectedAccountType | null
    bban: string | null
    iban: string | null
    countryCode: string | null
    bankName: string | null
    bic: string | null
    swift: string | null
    website: string | null
    currency: string | null
    business_Address: string | null
    business_PostalCode: string | null
    business_City: string | null
    business_Phone: string | null
    business_PhonePrefix: string | null
    business_TaxID: string | null
    individual_Title: string | null
    individual_Firstname: string | null
    individual_Lastname: string | null
    individual_Email: string | null
    individual_Phone: string | null
    individual_PhonePrefix: string | null
    individual_BirthDay: string | null
    individual_City: string | null
    individual_CountryCode: string | null
    individual_Address: string | null
    individual_Zipcode: string | null
    purposeDescription: string | null
    email: string | null
    info: string | null
    lastPayoutsEnabledCheck: string | null
    insertTime: string
    updateTime: string
    minBalanceAfterPayout: number
    minDaysBetweenPayouts: number

    tosIsAccepted: string | null
    zimplePayPartialIsAccepted: string | null
    signature_Completed: string | null

    isArchived: boolean
  }

  export interface Statistics {
    date: string
    paymentCount: number
    revenueSum: number
    paidOutSum: number
    paidOutCount: number
  }

  export namespace Statistics {
    export type Query = {
      startDate: string
      endDate: string
      groupByTime: 'Minute' | 'Hour' | 'Day' | 'Month'
      platformID?: string
    }
  }
}

export const ConnectedAccountGroups = {
  'Account information': {
    icon: Store,
    color: ColorPalette.pink,
    isMainGroup: true,
    mainColumn: 'name',
  },
  Business: {
    icon: Building2,
    color: ColorPalette.red,
    mainColumn: 'businessName',
  },
  Balance: {
    icon: Banknote,
    color: ColorPalette.green,
    mainColumn: 'balance',
  },
  Representative: {
    icon: CircleUserRound,
    color: ColorPalette.blue,
    mainColumn: 'individual_Firstname',
  },
  'Payout enabled': {
    icon: HandCoins,
    color: ColorPalette.brown,
    mainColumn: 'payoutsEnabled',
  },
  'Bank details': {
    icon: LucidePiggyBank,
    color: ColorPalette.purple,
    mainColumn: 'bban',
  },
  Time: {
    icon: Clock,
    color: ColorPalette.black,
    mainColumn: 'insertTime',
  },
} satisfies Groups<ConnectedAccounts.ListItem>

// I know its ugly but using chatGPT to generate the schema. see index.ts...
export const ConnectedAccountListSchema: JSSchema<
  ConnectedAccounts.ListItem,
  typeof ConnectedAccountGroups
> = {
  name: 'Connected Account',
  title: 'Connected Accounts',
  groups: ConnectedAccountGroups,
  fields: {
    email: {
      type: 'string',
      humanReadable: 'Email for attachments',
      smartSearchType: 'Lax',
      metaType: 'email',
      meta: {
        group: 'Account information',
      },
      sensitive: true,
    },
    signature_Completed: {
      type: 'date',
      humanReadable: 'Signature Completed at',
      meta: {
        group: 'Payout enabled',
      },
    },
    zimplePayPartialIsAccepted: {
      type: 'date',
      humanReadable: 'ZimplePay Partial Accepted at',
      meta: {
        group: 'Payout enabled',
      },
    },
    tosIsAccepted: {
      type: 'date',
      humanReadable: 'TOS Accepted at',
      meta: {
        group: 'Payout enabled',
      },
    },
    lastPayoutsEnabledCheck: {
      type: 'date',
      humanReadable: 'Last Payouts Enabled Check',
      meta: {
        group: 'Payout enabled',
      },
    },
    name: {
      type: 'string',
      humanReadable: 'Name',
      smartSearchType: 'Lax',
      meta: {
        group: 'Account information',
      },
      specificCell:
        (self) =>
        ({ row }) => {
          return h(
            ColumnLink,
            {
              id: row.original.connectedAccountID,
              pathName: 'account',
              content: row.original.name,
            },
            () => row.original.name
          )
        },
      sensitive: true,
    },
    purposeDescription: {
      type: 'string',
      humanReadable: 'Purpose Description',
      smartSearchType: 'Lax',
      meta: {
        group: 'Account information',
      },
      sensitive: true,
    },
    connectedAccountID: {
      type: 'string',
      humanReadable: 'Connected Account ID',
      primaryKey: true,
      meta: {
        group: 'Account information',
      },
      formattedCell(value: string) {
        return h(
          ColumnLink,
          {
            content: value,
            id: value,
            pathName: 'account',
          },
          () => value
        )
      },
    },
    paymentCount: {
      type: 'number',
      humanReadable: 'Payment Count',
      meta: {
        group: 'Balance',
      },
    },
    revenueSum: {
      type: 'number',
      humanReadable: 'Revenue',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Balance',
      },
    },
    paidOutSum: {
      type: 'number',
      humanReadable: 'Paid out',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Balance',
      },
    },
    balance: {
      type: 'number',
      humanReadable: 'Balance',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Balance',
      },
      specificCell:
        () =>
        ({ row }) => {
          return h(MoneyFormatted, {
            amount: row.original.balance,
            oneSize: true,
            currency: row.original.currency,
            showColor: true,
          })
        },
    },
    availableForPayouts: {
      type: 'number',
      humanReadable: 'Available for payout',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Balance',
      },
      specificCell:
        () =>
        ({ row }) => {
          return h(MoneyFormatted, {
            amount: row.original.availableForPayouts,
            oneSize: true,
            currency: row.original.currency,
            showColor: true,
          })
        },
    },
    platformID: {
      type: 'string',
      humanReadable: 'Platform ID',
      meta: {
        group: 'Account information',
      },
    },
    externalReferenceID: {
      type: 'string',
      humanReadable: 'External Reference ID',
      meta: {
        group: 'Account information',
      },
    },
    payoutsEnabled: {
      type: 'boolean',
      humanReadable: 'Payouts Enabled',
      meta: {
        group: 'Payout enabled',
      },
    },
    salesEnabled: {
      type: 'boolean',
      humanReadable: 'Sales Enabled',
    },
    businessName: {
      type: 'string',
      humanReadable: 'Name',
      smartSearchType: 'Lax',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    businessType: {
      type: 'enum',
      humanReadable: 'Type',
      options: ConnectedAccountTypeList,
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    bban: {
      type: 'string',
      humanReadable: 'BBAN',
      meta: {
        group: 'Bank details',
      },
      sensitive: true,
    },
    iban: {
      type: 'string',
      humanReadable: 'IBAN',
      meta: {
        group: 'Bank details',
      },
      sensitive: true,
    },
    countryCode: {
      type: 'string',
      humanReadable: 'Country Code',
      meta: {
        group: 'Business',
      },
      metaType: 'countryCode',
    },
    bankName: {
      type: 'string',
      humanReadable: 'Bank',
      smartSearchType: 'Lax',
      meta: {
        group: 'Bank details',
      },
      sensitive: true,
    },
    bic: {
      type: 'string',
      humanReadable: 'BIC',
      meta: {
        group: 'Bank details',
      },
      sensitive: true,
    },
    swift: {
      type: 'string',
      humanReadable: 'SWIFT Code',
      meta: {
        group: 'Bank details',
      },
      sensitive: true,
    },
    website: {
      type: 'string',
      humanReadable: 'Website URL',
      smartSearchType: 'Lax',
      metaType: 'url',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    currency: {
      type: 'enum',
      humanReadable: 'Currency',
      options: CurrencyList,
      meta: {
        group: 'Business',
      },
    },
    business_Address: {
      type: 'string',
      humanReadable: 'Address',
      smartSearchType: 'Lax',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    business_PostalCode: {
      type: 'string',
      humanReadable: 'Postal Code',
      smartSearchType: 'Lax',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    business_City: {
      type: 'string',
      humanReadable: 'City',
      smartSearchType: 'Lax',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    business_Phone: {
      type: 'string',
      metaType: 'phone',
      humanReadable: 'Phone Number',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    business_PhonePrefix: {
      type: 'string',
      metaType: 'phone',
      humanReadable: 'Phone Number Prefix',
      meta: {
        group: 'Business',
      },
    },
    business_TaxID: {
      type: 'string',
      humanReadable: 'Organization nr',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    individual_Title: {
      type: 'string',
      humanReadable: 'Role',
      smartSearchType: 'Lax',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_Firstname: {
      type: 'string',
      humanReadable: 'First Name',
      smartSearchType: 'Lax',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_Lastname: {
      type: 'string',
      humanReadable: 'Last Name',
      smartSearchType: 'Lax',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_Email: {
      type: 'string',
      humanReadable: 'Email',
      smartSearchType: 'Lax',
      metaType: 'email',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_Phone: {
      type: 'string',
      humanReadable: 'Phone Number',
      metaType: 'phone',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_PhonePrefix: {
      type: 'string',
      humanReadable: 'Phone Number Prefix',
      metaType: 'phone',
      meta: {
        group: 'Representative',
      },
    },
    individual_BirthDay: {
      type: 'date',
      humanReadable: 'Birthday',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_City: {
      type: 'string',
      humanReadable: 'City',
      smartSearchType: 'Lax',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_CountryCode: {
      type: 'string',
      humanReadable: 'Country Code',
      meta: {
        group: 'Representative',
      },
      metaType: 'countryCode',
    },
    individual_Address: {
      type: 'string',
      humanReadable: 'Address',
      smartSearchType: 'Lax',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    individual_Zipcode: {
      type: 'string',
      humanReadable: 'Zipcode',
      meta: {
        group: 'Representative',
      },
      sensitive: true,
    },
    info: {
      type: 'string',
      humanReadable: 'Additional Info',
      smartSearchType: 'Lax',
      meta: {
        group: 'Payout enabled',
        hideHeaderInCell: true,
      },
      sensitive: true,
    },

    minBalanceAfterPayout: {
      type: 'number',
      humanReadable: 'Min Balance After Payout',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Payout enabled',
      },
    },
    minDaysBetweenPayouts: {
      type: 'number',
      humanReadable: 'Minimum Days Between Payouts',
      meta: {
        group: 'Payout enabled',
      },
    },
    isArchived: {
      type: 'boolean',
      humanReadable: 'Arkivert',
      meta: {
        group: 'Account information',
      },
      metaType: 'RedYesGreenNo',
    },
    updateTime: {
      type: 'date',
      humanReadable: 'Updated at',
      meta: {
        group: 'Time',
      },
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Created at',
      meta: {
        group: 'Time',
      },
      isDefaultSortColumn: true,
    },
  },
  actions: {
    'rerun-webhook': {
      cell: ({ row }) =>
        h(RerunWebhookButton, { connectedAccountID: row.original.connectedAccountID }),
    },
    goto: {
      cell: ({ row }) => {
        const account = row.original

        return h(
          'div',
          { style: { display: 'flex', justifyContent: 'end' } },
          h(DataTableGoto, {
            name: 'Account',
            id: account.connectedAccountID,
            pathName: 'account',
          })
        )
      },
    },
  },
}

export const ConnectedAccountFullSchema: JSSchema<
  ConnectedAccounts.Full,
  typeof ConnectedAccountGroups
> = {
  ...ConnectedAccountListSchema,
  title: 'Connected Account',
}
